<template>
  <a-modal
    v-model="dialogVisible"
    :title="modalTitle"
    width="1186px"
    :mask-closable="false"
    @cancel="onCancel"
    @ok="onOk"
  >
    <el-form ref="form" :model="form" label-suffix="：" label-width="160px">
      <el-row>
        <el-col
          v-for="item in formTemplate"
          :key="item.defineCode"
          :span="item.span"
        >
          <el-form-item
            :label="item.columnName"
            :prop="item.defineCode"
            :rules="item.rules"
          >
            <div v-if="readOnly">
              <template v-if="item.component === 'upload'">
                <upload
                  v-if="form.attachmentFileIds && form.attachmentFileIds[0]"
                  :props-data="item.elAttrs"
                >
                  <template>
                    支持pdf/doc/docx/xls/xlsx/ppt/pptx类型文件上传，word类型文件最大50MB；其他类型文件单个最大200MB；最多上传1个。
                  </template>
                </upload>
                <span v-else>-</span>
              </template>
              <div v-else-if="item.component === 'tree-select'">
                {{
                  form.categoryNameTree && form.categoryNameTree.length > 0
                    ? form.categoryNameTree.join('>')
                    : '-'
                }}
              </div>
              <div v-else-if="item.moneyInCn" class="text-main">
                {{ form[item.defineCode] | moneyFilter }}元
              </div>
              <div v-else class="text-main">
                {{ form[item.defineCode] || '-' }}
              </div>
              <money-in-cn
                v-if="item.moneyInCn"
                :money="form[item.defineCode] || 0"
              />
            </div>
            <template v-else>
              <!-- 针对品目名称处特殊处理，放置选择已有品目按钮 -->
              <div
                v-if="item.columnType === materialColumnTypeDict.NAME.value"
                class="flex row-center"
              >
                <el-input
                  v-model="form[item.defineCode]"
                  placeholder="请输入"
                  :disabled="item.readOnly && item.readOnly(form)"
                  v-bind="item.elAttrs"
                />
                <a-button
                  type="primary"
                  ghost
                  class="m-l-10"
                  @click="openSelectMaterial"
                >
                  选择已有品目
                </a-button>
              </div>
              <el-input
                v-else-if="item.component === 'input-textarea'"
                v-model="form[item.defineCode]"
                type="textarea"
                maxlength="500"
                :disabled="item.readOnly && item.readOnly(form)"
                v-bind="item.elAttrs"
              />
              <el-input-number
                v-else-if="item.component === 'input-number'"
                v-model="form[item.defineCode]"
                :disabled="item.readOnly && item.readOnly(form)"
                :min="0.0"
                :max="99999999999"
                :precision="2"
                v-bind="item.elAttrs"
                @change="handleInputNumberChange(item)"
              />
              <upload
                v-else-if="item.component === 'upload'"
                :props-data="item.elAttrs"
              />
              <div v-else-if="item.component === 'detail'">
                <div v-if="item.moneyInCn" class="text-main">
                  {{ form[item.defineCode] | moneyFilter }}元
                </div>
                <div v-else class="text-main">{{ form[item.defineCode] }}</div>
                <money-in-cn
                  v-if="item.moneyInCn"
                  :money="form[item.defineCode] || 0"
                />
              </div>
              <el-select
                v-else-if="item.component === 'select'"
                v-model="form[item.defineCode]"
                v-bind="item.elAttrs"
                :disabled="item.readOnly && item.readOnly(form)"
                style="width: 100%"
              >
                <el-option
                  v-for="item in unitOptions"
                  :key="item.value"
                  :label="item.label"
                  :value="item.value"
                />
              </el-select>
              <tree-select
                v-else-if="item.component === 'tree-select'"
                v-model="form[item.defineCode]"
                placeholder="请选择"
                :disabled="item.readOnly && item.readOnly(form)"
                :options="classifyTree"
                no-options-text="没有可选项"
              />
              <el-input
                v-else
                v-model="form[item.defineCode]"
                placeholder="请输入"
                :disabled="item.readOnly && item.readOnly(form)"
                maxlength="200"
                v-bind="item.elAttrs"
              />
            </template>
          </el-form-item>
        </el-col>
      </el-row>
    </el-form>

    <!-- 选择已有品目 -->
    <select-material
      v-model="selectMaterialVisible"
      @confirm="onConfirmSelectMaterial"
    />
  </a-modal>
</template>

<script>
// 组件
import Upload from '@/components/common/Upload'
import MoneyInCn from '@/components/common/MoneyInCn'
import SelectMaterial from '@/components/common/PurchaseListComponent/SelectMaterial'
import TreeSelect from '@riophae/vue-treeselect'
import '@riophae/vue-treeselect/dist/vue-treeselect.css'
// 工具
import { strToThousands } from '@/utils/utils'
import { requiredRule } from '@/utils/form-validate'
import { unitOptions } from '@/dictionary/project/common'
import { getMaterialClassifyTreeApi } from '@/api/material/management'
import { cloneDeep } from 'lodash'
import {
  materialColumnTypeDict,
  materialTemplateDefineTypeDict,
  materialTemplateKeySupplierVisibleDict
} from '@/dictionary/material/template.js'

/**
 * @description 新增或编辑采购项
 */
export default {
  name: 'PurchaseListItem',

  components: { Upload, MoneyInCn, SelectMaterial, TreeSelect },

  filters: {
    moneyFilter(value) {
      return strToThousands(value || '0')
    }
  },

  model: {
    prop: 'visible',
    event: 'change'
  },

  props: {
    visible: {
      type: Boolean,
      default: false
    },

    info: {
      type: Object,
      default: () => {}
    },

    // 外部传入的表单模板
    config: {
      type: Array,
      default: () => []
    },

    // 上传组件需要的定义参数
    uploadParams: {
      type: Object,
      default: () => {
        return {
          bizType: '',
          attachmentType: ''
        }
      }
    },

    // 只读状态
    readOnly: {
      type: Boolean,
      default: false
    },

    // 供应商视角
    inSupplier: {
      type: Boolean,
      default: false
    },

    // 是否是美元
    isDollar: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      materialColumnTypeDict,

      // 分类树
      classifyTree: [],
      defaultProps: {
        children: 'children',
        label: 'categoryName'
      },

      form: {},

      // 字段模板
      formTemplate: [],

      unitOptions: [], // 品目单位数据字典

      // 控制显示选择已有品目弹窗
      selectMaterialVisible: false
    }
  },

  computed: {
    dialogVisible: {
      get() {
        return this.visible
      },
      set(val) {
        this.$emit('change', val)
      }
    },

    // 采购项信息是否为空
    infoEmpty() {
      return Object.keys(this.info).length === 0
    },

    // 弹窗标题
    modalTitle() {
      return this.readOnly
        ? '采购项详情'
        : this.infoEmpty
          ? '新增采购项'
          : '编辑采购项'
    },

    // 品目名称
    materielName() {
      return (
        this.config.find(
          item => item.columnType === materialColumnTypeDict.NAME.value
        ) || {}
      )
    },

    // 品目编码
    materielCode() {
      return (
        this.config.find(
          item => item.columnType === materialColumnTypeDict.CODE.value
        ) || {}
      )
    },

    // 参数型号
    materielModel() {
      return (
        this.config.find(
          item => item.columnType === materialColumnTypeDict.MODEL.value
        ) || {}
      )
    },

    // 控制价的模板配置
    controlPriceConfig() {
      return (
        this.config.find(
          item => item.columnType === materialColumnTypeDict.CONTROL_PRICE.value
        ) || {}
      )
    },

    // 品目单位
    unitType() {
      return (
        this.config.find(
          item => item.columnType === materialColumnTypeDict.UNIT.value
        ) || {}
      )
    },

    // 品目数量
    materielNum() {
      return (
        this.config.find(
          item => item.columnType === materialColumnTypeDict.NUM.value
        ) || {}
      )
    },

    // 品目单价
    budgetUnitPrice() {
      return (
        this.config.find(
          item => item.columnType === materialColumnTypeDict.UNIT_PRICE.value
        ) || {}
      )
    }
  },

  watch: {
    config: {
      handler(val) {
        if (val?.length > 0) {
          this.initFormTemplate()
        }
      },
      deep: true,
      immediate: true
    },

    form: {
      handler(val) {
        //
      },
      deep: true,
      immediate: true
    },

    isDollar: {
      handler: function(val) {
        this.initFormTemplate()
      },
      immediate: true
    },

    'form.materielNum'(val) {
      // 如果数量修改，在有单价的情况下，可以计算总价
      if (this.form.budgetUnitPrice) {
        const price = Number(
          ((val * 100 * (this.form.budgetUnitPrice * 100)) / 10000).toFixed(2)
        )
        this.form.totalPrice = isNaN(price) ? 0 : price
      }
    },

    'form.budgetUnitPrice'(val) {
      // 如果单价修改，在有数量的情况下，可以计算总价
      if (this.form.materielNum) {
        const price = Number(
          ((val * 100 * (this.form.materielNum * 100)) / 10000).toFixed(2)
        )
        this.form.totalPrice = isNaN(price) ? 0 : price
      }
    },

    'form.attachmentFileIds'() {
      const uploadConfig = this.formTemplate[this.formTemplate.length - 1]
      uploadConfig.elAttrs.fileIdBefore =
        this.form[uploadConfig.defineCode]?.length > 0
          ? this.form[uploadConfig.defineCode][0]
          : ''
    },

    visible(val) {
      if (val) {
        this.form = Object.assign(
          {},
          this.initForm(this.formTemplate),
          this.info
        )
        this.getClassifyTree()
      } else {
        this.form = this.initForm(this.formTemplate)
      }
    }
  },

  methods: {
    /**
     * 将元改成美元
     */
    changeToDollarUnit(label) {
      if (label.includes('元')) {
        return label.replace('元', '美元')
      }
      return label + '（美元）'
    },

    /**
     * 初始化表单配置
     */
    initFormTemplate() {
      this.formTemplate = [
        {
          columnName: this.materielName.columnName,
          defineCode: this.materielName.defineCode,
          span: 24,
          rules: [
            { required: true, message: '请输入', trigger: ['blur', 'change'] },
            {
              validator: (rule, value, callback) => {
                if (value?.length > 50) {
                  callback(
                    new Error(`${this.materielName.columnName}限制输入50个字符`)
                  )
                  return
                }
                callback()
              },
              trigger: 'blur'
            }
          ],
          columnType: materialColumnTypeDict.NAME.value,
          readOnly: form => Boolean(form.itemId)
        },
        {
          columnName: '分类',
          defineCode: 'categoryId',
          span: 12,
          component: 'tree-select',
          columnType: -1,
          readOnly: form => Boolean(form.itemId)
        },
        {
          columnName: this.materielCode.columnName,
          defineCode: this.materielCode.defineCode,
          span: 12,
          columnType: materialColumnTypeDict.CODE.value,
          readOnly: form => Boolean(form.itemId)
        },
        this.materielModel.isEnable
          ? {
            columnName: this.materielModel.columnName,
            defineCode: this.materielModel.defineCode,
            span: 12
          }
          : undefined,
        {
          columnName: this.unitType.columnName,
          defineCode: this.unitType.defineCode,
          span: 12,
          component: 'select',
          elAttrs: {
            filterable: true,
            remote: true,
            allowCreate: true,
            defaultFirstOption: true,
            placeholder: '请输入单位，示例：台、个、千米',
            remoteMethod: this.getUnitOptions
          },
          rules: requiredRule('请输入'),
          columnType: materialColumnTypeDict.UNIT.value,
          readOnly: form => Boolean(form.itemId)
        },
        {
          columnName: this.materielNum.columnName,
          defineCode: this.materielNum.defineCode,
          span: this.inSupplier ? 12 : 6,
          component: 'input-number',
          elAttrs: { controlsPosition: 'right', placeholder: '请输入' },
          columnType: materialColumnTypeDict.NUM.value,
          rules: [
            requiredRule('请输入'),
            {
              validator: (rule, value, callback) => {
                if (Number(value) === 0) {
                  callback(new Error(`${this.materielNum.columnName}不允许为0`))
                  return
                }
                callback()
              },
              trigger: 'blur'
            }
          ]
        },
        this.inSupplier
          ? null
          : {
            columnName: this.isDollar
              ? this.changeToDollarUnit(this.budgetUnitPrice.columnName)
              : this.budgetUnitPrice.columnName,
            defineCode: this.budgetUnitPrice.defineCode,
            span: 6,
            component: 'input-number',
            elAttrs: { controlsPosition: 'right', placeholder: '请输入' },
            columnType: materialColumnTypeDict.UNIT_PRICE.value,
            rules: [
              requiredRule('请输入'),
              {
                validator: (rule, value, callback) => {
                  if (Number(value) === 0) {
                    callback(
                      new Error(`${this.budgetUnitPrice.columnName}不允许为0`)
                    )
                    return
                  }
                  callback()
                },
                trigger: 'blur'
              }
            ]
          },
        this.inSupplier
          ? null
          : {
            columnName: `预算总价${this.isDollar ? '(美元)' : ''}`,
            defineCode: 'totalPrice',
            span: 12,
            component: 'detail',
            moneyInCn: !this.isDollar,
            columnType: -1
          },
        this.controlPriceConfig.isEnable
          ? {
            columnName: `${this.controlPriceConfig.columnName.replace(
              '（元）',
              ''
            )}${this.isDollar ? '(美元)' : ''}`,
            defineCode: this.controlPriceConfig.defineCode,
            moneyInCn: !this.isDollar,
            columnType: materialColumnTypeDict.CONTROL_PRICE.value,
            span: 12,
            component: 'input-number',
            elAttrs: { controlsPosition: 'right', placeholder: '请输入' },
            rules: [
              requiredRule('请输入'),
              {
                validator: (rule, value, callback) => {
                  if (Number(value) === 0) {
                    callback(
                      new Error(`${this.budgetUnitPrice.columnName}不允许为0`)
                    )
                    return
                  }
                  callback()
                },
                trigger: 'blur'
              }
            ]
          }
          : null,
        {
          columnName: '对供应商备注',
          defineCode: 'remarks',
          span: 12,
          component: 'input-textarea',
          columnType: -1,
          elAttrs: {
            rows: 4,
            placeholder: '请输入',
            maxlength: 500
          }
        },
        this.inSupplier
          ? null
          : {
            columnName: '内部备注',
            defineCode: 'insideRemark',
            span: 12,
            component: 'input-textarea',
            columnType: -1,
            elAttrs: { rows: 4, placeholder: '请输入', maxlength: 500 }
          },
        {
          columnName: '附件',
          defineCode: 'attachmentFileIds',
          span: 24,
          component: 'upload',
          columnType: -1,
          elAttrs: {
            limit: 1,
            multiple: true,
            bizType: this.uploadParams.bizType,
            attachmentType: this.uploadParams.attachmentType,
            uploadMode: 'fileId',
            fileIdBefore: '',
            onSuccess: fileId => (this.form.attachmentFileIds = [fileId]),
            onCompleteDelete: () => (this.form.attachmentFileIds = []),
            disabled: this.readOnly
          }
        }
      ].filter(value => value)

      this.config.forEach(item => {
        // 对于非自定义的，根据定好的顺序和相关参数进行放置
        if (item.columnType !== materialColumnTypeDict.CUSTOM.value) {
          const index = this.formTemplate.findIndex(
            v => v.columnType === item.columnType
          )
          if (index < 0) return
          this.formTemplate[index] = Object.assign(
            {},
            this.formTemplate[index],
            item
          )
        } else if (
          item.defineType === materialTemplateDefineTypeDict.SPECIFY.value &&
          item.isEnable
        ) {
          // 先根据defineCode查找是否已存在
          const index = this.formTemplate.findIndex(
            t => t.defineCode === item.defineCode
          )
          if (index > -1) return
          // 如果在供应商视角，则要屏蔽供应商不可见的字段
          if (
            this.inSupplier &&
            item.supplierVisible ===
              materialTemplateKeySupplierVisibleDict.NO.value
          ) {
            return
          }
          // 对于已启用的、自定义的且是采购人指定的，生成表单模板配置并放在上传附件之前
          item.span = 12
          this.formTemplate.splice(this.formTemplate.length - 1, 0, item)
        }
      })
    },

    /**
     * 获取品目分类树
     */
    getClassifyTree() {
      getMaterialClassifyTreeApi()
        .then(res => {
          const _data = res.data || {}
          // 基础分类树
          const categoryTree = _data.categoryTree || []
          function transferLabel(list) {
            const arr = []
            list.forEach(item => {
              item.id = item.categoryId
              item.label = item.categoryName
              if (item.children?.length > 0) {
                transferLabel(item.children)
              } else {
                delete item.children
              }

              arr.push(item)
            })
            return arr
          }
          this.classifyTree = transferLabel(categoryTree)
        })
        .catch(err => {
          console.error('获取品目分类树失败', err)
          if (err.message) this.$message.error(err.message)
        })
    },

    /**
     * 初始化表单
     */
    initForm(formTemplate) {
      if (!formTemplate?.length) return {}

      const form = {}
      formTemplate.forEach(item => {
        form[item.defineCode] = undefined
      })
      return form
    },

    /**
     * 关闭弹窗
     */
    onCancel() {
      this.dialogVisible = false
      this.$refs.form.clearValidate()
      this.$refs.form.resetFields()
    },

    /**
     * 点击确定
     */
    onOk() {
      if (this.readOnly) this.onCancel()

      this.getCategoryPath()
      this.$refs.form.validate(valid => {
        if (!valid) return

        this.$emit('onConfirm', cloneDeep(this.form))
        this.onCancel()
      })
    },

    // 模拟远程获取单位
    getUnitOptions(query) {
      query = query.trim()
      if (!query) {
        this.unitOptions = []
        return
      }

      this.unitOptions = unitOptions.filter(unit => {
        return unit.label.indexOf(query) > -1
      })
    },

    /**
     * 打开选择品目弹窗
     */
    openSelectMaterial() {
      this.selectMaterialVisible = true
    },

    /**
     * 确认选择的品目回调
     */
    onConfirmSelectMaterial(material) {
      // 根据模板中的columnType来确定品目名称、编码和单位
      this.config.forEach(item => {
        // 品目名称
        if (item.columnType === materialColumnTypeDict.NAME.value) {
          this.form[item.defineCode] = material.itemName
        }
        // 品目编码
        if (item.columnType === materialColumnTypeDict.CODE.value) {
          this.form[item.defineCode] = material.itemCode
        }
        // 单位
        if (item.columnType === materialColumnTypeDict.UNIT.value) {
          this.form[item.defineCode] = material.itemUnit
        }
      })
      // 保存分类id
      this.form.categoryId = material.categoryId
      this.form.itemId = material.itemId
      // this.form = Object.assign({}, this.form, material)
    },

    /**
     * 查找节点在树中的路径
     */
    getCategoryPath() {
      function treeFindPath(tree, func, prop = 'label', path = []) {
        if (!tree) return [] // 空判断
        for (const data of tree) {
          path.push(data[prop])
          if (func(data)) return path
          if (data.children) {
            const findChildren = treeFindPath(data.children, func, prop, path)
            if (findChildren.length) return findChildren
          }
          path.pop()
        }
        return []
      }
      const findSingle = data => {
        if (data.id === this.form.categoryId) {
          return true
        }
      }
      this.form.categoryNameTree = treeFindPath(this.classifyTree, findSingle)
      this.form.categoryIdTree = treeFindPath(
        this.classifyTree,
        findSingle,
        'id'
      )
    },

    handleInputNumberChange(item) {
      // 数量变化或单价变化时，要计算总价
      if (item.defineCode === this.budgetUnitPrice.defineCode) {
        const price = Number(
          (
            (this.form[item.defineCode] *
              100 *
              (this.form[this.materielNum.defineCode] * 100)) /
            10000
          ).toFixed(2)
        )
        this.form.totalPrice = isNaN(price) ? 0 : price
      }
      if (item.defineCode === this.materielNum.defineCode) {
        const price = Number(
          (
            (this.form[item.defineCode] *
              100 *
              (this.form[this.budgetUnitPrice.defineCode] * 100)) /
            10000
          ).toFixed(2)
        )
        this.form.totalPrice = isNaN(price) ? 0 : price
      }
    }
  }
}
</script>

<style scoped></style>
